<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import dayjs from 'dayjs';
import RecurringPicker from './RecurringPicker.vue';
import { stores } from '@/util/stores.js';
import { useToast } from '@/composables/useToast.ts';
import TimePicker from '@/components/TimePicker/TimePicker.vue';
import { getAvailabilityBlockTypeForScope, getBlockingBlockTypeForScope, isAvailabilityBlockType, normalizeDayCodes, type AvailabilityEntry, type DayCode, type ScopeKind } from '@/types/availability';

interface EditableAvailabilityEntry extends AvailabilityEntry {
	groupNumber?: number | null;
	clientNumber?: number | null;
}

const props = withDefaults(
	defineProps<{
		mode?: 'create' | 'edit';
		initialData?: EditableAvailabilityEntry | null;
		preselectedDates?: string[];
	}>(),
	{
		mode: 'create',
		initialData: null,
		preselectedDates: () => []
	}
);

const emit = defineEmits<{
	saved: [value?: unknown];
	cancelled: [];
	deleted: [];
}>();

const toast = useToast();
const preselectedDates = computed(() => props.preselectedDates ?? []);

// Form state
const formType = ref<'available' | 'block'>('available');
const startDate = ref('');
const endDate = ref('');
const allDay = ref(true);
const startTime = ref('09:00');
const endTime = ref('17:00');
const scope = ref<ScopeKind>('global');
const selectedGroupId = ref<number | null>(null);
const selectedClientId = ref<number | null>(null);
const isRecurring = ref(false);
const recurringDays = ref<DayCode[]>([]);
const notes = ref('');

// UI state
const saving = ref(false);
const deleting = ref(false);
const groupsLoading = ref(false);
const showGroupValidation = ref(false);

function timeStringToDate(timeStr: string | null | undefined) {
	if (!timeStr) return null;
	const [hh, mm, ss] = String(timeStr).split(':');
	return dayjs()
		.hour(Number(hh || 0))
		.minute(Number(mm || 0))
		.second(Number(ss || 0))
		.millisecond(0)
		.toDate();
}

// Get user's groups for scope selection
const userGroups = computed(() => {
	const raw: Array<any> = stores.usersStore.groups || [];
	return raw
		.map((g) => g?.group || g)
		.filter((g) => g && (g.groupNumber || g.groupNumber === 0))
		.filter((g) => g.groupNumber !== 0)
		.map((g) => ({
			id: Number(g.groupNumber),
			name: g.shortName ? `${g.shortName} — ${g.name}` : g.name
		}));
});

// Computed block type based on form selections
const blockType = computed(() => {
	return formType.value === 'available' ? getAvailabilityBlockTypeForScope(scope.value) : getBlockingBlockTypeForScope(scope.value);
});

// Date range display
const dateRangeDisplay = computed(() => {
	if (!startDate.value) return '';
	const start = dayjs(startDate.value);
	const end = endDate.value ? dayjs(endDate.value) : start;

	if (start.isSame(end, 'day')) {
		return start.format('MMMM D, YYYY');
	}
	return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
});

// Validation
const isValid = computed(() => {
	if (!startDate.value) return false;
	if (!allDay.value && (!startTime.value || !endTime.value)) return false;
	if (!allDay.value) {
		const s = timeStringToDate(startTime.value);
		const e = timeStringToDate(endTime.value);
		if (s && e && !dayjs(e).isAfter(dayjs(s))) return false;
	}
	if (scope.value === 'group' && !selectedGroupId.value) return false;
	if (scope.value === 'client' && !selectedClientId.value) return false;
	if (isRecurring.value && recurringDays.value.length === 0) return false;
	return true;
});

// Methods
function initFromProps() {
	formType.value = 'available';
	startDate.value = '';
	endDate.value = '';
	allDay.value = true;
	startTime.value = '09:00';
	endTime.value = '17:00';
	scope.value = 'global';
	selectedGroupId.value = null;
	selectedClientId.value = null;
	isRecurring.value = false;
	recurringDays.value = [];
	notes.value = '';

	if (preselectedDates.value.length > 0 && !props.initialData) {
		const sorted = [...preselectedDates.value].sort();
		startDate.value = sorted[0] || '';
		endDate.value = sorted[sorted.length - 1] || startDate.value;

		const start = dayjs(startDate.value);
		const end = dayjs(endDate.value || startDate.value);
		const spanDays = end.diff(start, 'day');
		const isContiguous = sorted.length === spanDays + 1;

		const dayCodeMap: Record<number, DayCode> = {
			0: 'SUN',
			1: 'MON',
			2: 'TUE',
			3: 'WED',
			4: 'THU',
			5: 'FRI',
			6: 'SAT'
		};

		const uniqueDayCodes: DayCode[] = Array.from(
			new Set(
				sorted
					.map((d) => {
						const parsed = dayjs(d);
						if (!parsed.isValid()) return null;
						return dayCodeMap[parsed.day()];
					})
					.filter((value): value is DayCode => value !== null)
			)
		);

		if (!isContiguous && spanDays >= 7 && uniqueDayCodes.length > 0) {
			isRecurring.value = true;
			recurringDays.value = uniqueDayCodes;
		} else {
			recurringDays.value = uniqueDayCodes;
		}
	}

	if (props.initialData) {
		const data = props.initialData;

		formType.value = isAvailabilityBlockType(data.blockType) ? 'available' : 'block';
		startDate.value = data.startDate;
		endDate.value = data.endDate || data.startDate;
		allDay.value = data.allDay !== false;
		startTime.value = data.startTime || '09:00';
		endTime.value = data.endTime || '17:00';
		isRecurring.value = data.recurring || false;
		recurringDays.value = normalizeDayCodes(data.daysOfWeek);
		notes.value = data.notes || '';

		if (data.client) {
			scope.value = 'client';
			selectedClientId.value = Number(data.client.clientNumber);
		} else if (data.group) {
			scope.value = 'group';
			selectedGroupId.value = Number(data.group.groupNumber);
		} else {
			scope.value = 'global';
		}
	}
}

async function handleSave() {
	if (scope.value === 'group' && !selectedGroupId.value) {
		showGroupValidation.value = true;
	}
	if (!isValid.value || saving.value) return;
	saving.value = true;

	const payload = {
		startDate: startDate.value,
		endDate: endDate.value || startDate.value,
		allDay: allDay.value,
		startTime: allDay.value ? null : startTime.value,
		endTime: allDay.value ? null : endTime.value,
		blockType: blockType.value,
		groupNumber: scope.value === 'group' ? selectedGroupId.value : null,
		clientNumber: scope.value === 'client' ? selectedClientId.value : null,
		recurring: isRecurring.value,
		daysOfWeek: isRecurring.value ? recurringDays.value.join(',') : null,
		notes: notes.value || null
	};

	let result;
	if (props.mode === 'edit' && props.initialData?.id) {
		result = await stores.availabilityStore.updateBlock(props.initialData.id, payload);
	} else {
		result = await stores.availabilityStore.saveBlock(payload);
	}

	if (result.success) {
		toast.add({
			severity: 'success',
			summary: formType.value === 'available' ? 'Availability saved' : 'Unavailable time saved',
			detail: 'Your calendar now reflects the selected dates and times.',
			life: 3000
		});
		emit('saved', result.data);
	} else {
		toast.add({
			severity: 'error',
			summary: 'Could not save availability',
			detail: result.error || 'Your availability could not be saved. Please try again.',
			life: 5000
		});
	}

	saving.value = false;
}

async function handleDelete() {
	if (!props.initialData?.id || deleting.value) return;

	deleting.value = true;

	const result = await stores.availabilityStore.deleteBlock(props.initialData.id);

	if (result.success) {
		toast.add({
			severity: 'success',
			summary: 'Availability entry removed',
			detail: 'The selected availability or blocked-time entry was removed from your calendar.',
			life: 3000
		});
		emit('deleted');
	} else {
		toast.add({
			severity: 'error',
			summary: 'Could not remove availability',
			detail: result.error || 'The calendar entry could not be removed. Please try again.',
			life: 5000
		});
	}

	deleting.value = false;
}

function handleCancel() {
	emit('cancelled');
}

const initialDataSignature = computed(() => {
	const data = props.initialData;
	if (!data) return '';

	return JSON.stringify({
		id: data.id ?? null,
		blockType: data.blockType ?? null,
		startDate: data.startDate,
		endDate: data.endDate,
		startTime: data.startTime ?? null,
		endTime: data.endTime ?? null,
		allDay: data.allDay ?? true,
		recurring: data.recurring ?? false,
		daysOfWeek: normalizeDayCodes(data.daysOfWeek),
		groupNumber: data.group?.groupNumber ?? data.groupNumber ?? null,
		clientNumber: data.client?.clientNumber ?? data.clientNumber ?? null,
		notes: data.notes ?? null
	});
});

const preselectedDatesSignature = computed(() => preselectedDates.value.join('|'));

onMounted(async () => {
	if (!stores.usersStore.groups || stores.usersStore.groups.length === 0) {
		groupsLoading.value = true;
		try {
			await stores.usersStore.usersGroups();
		} finally {
			groupsLoading.value = false;
		}
	}
});

watch(
	() => [initialDataSignature.value, preselectedDatesSignature.value],
	() => {
		initFromProps();
	},
	{ immediate: true }
);
</script>

<template>
	<form class="flex flex-col gap-5" @submit.prevent="handleSave">
		<!-- Type Toggle -->
		<div class="flex flex-col gap-2">
			<label class="text-[0.8125rem] font-semibold text-color">Type</label>
			<div class="grid grid-cols-2 gap-2">
				<button
					type="button"
					class="flex items-center justify-center gap-2 py-3 px-4 border-2 border-surface rounded-xl bg-[var(--egs-content-background)] text-color-secondary text-sm font-medium cursor-pointer transition-all hover:border-primary"
					:class="{ '!bg-green-500/10 !border-green-500 !text-green-700': formType === 'available' }"
					@click="formType = 'available'"
				>
					<font-awesome-icon :icon="['fas', 'check']" />
					<span>Available</span>
				</button>
				<button
					type="button"
					class="flex items-center justify-center gap-2 py-3 px-4 border-2 border-surface rounded-xl bg-[var(--egs-content-background)] text-color-secondary text-sm font-medium cursor-pointer transition-all hover:border-primary"
					:class="{ '!bg-red-500/10 !border-red-500 !text-red-700': formType === 'block' }"
					@click="formType = 'block'"
				>
					<font-awesome-icon :icon="['fas', 'ban']" />
					<span>Blocked</span>
				</button>
			</div>
		</div>

		<!-- Date Range -->
		<div class="flex flex-col gap-2">
			<label class="text-[0.8125rem] font-semibold text-color">Date Range</label>
			<div class="grid grid-cols-2 gap-3">
				<div class="flex flex-col">
					<label class="text-xs text-color-secondary mb-1">Start Date</label>
					<input
						type="date"
						v-model="startDate"
						class="py-2.5 px-3 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color text-sm transition-colors focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
						required
					/>
				</div>
				<div class="flex flex-col">
					<label class="text-xs text-color-secondary mb-1">End Date</label>
					<input
						type="date"
						v-model="endDate"
						:min="startDate"
						class="py-2.5 px-3 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color text-sm transition-colors focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
					/>
				</div>
			</div>
			<p v-if="dateRangeDisplay" class="text-[0.8125rem] text-primary font-medium">{{ dateRangeDisplay }}</p>
		</div>

		<!-- Time Range -->
		<div class="flex flex-col gap-2">
			<div class="flex items-center justify-between">
				<label class="text-[0.8125rem] font-semibold text-color">Time</label>
				<label class="flex items-center gap-2 text-sm text-color cursor-pointer">
					<input type="checkbox" v-model="allDay" class="w-4 h-4 accent-primary" />
					<span>All Day</span>
				</label>
			</div>
			<div v-if="!allDay" class="grid grid-cols-2 gap-3">
				<div class="flex flex-col">
					<label class="text-xs text-color-secondary mb-1">Start Time</label>
					<TimePicker v-model="startTime" :minuteStep="5" placeholder="Start time" />
				</div>
				<div class="flex flex-col">
					<label class="text-xs text-color-secondary mb-1">End Time</label>
					<TimePicker v-model="endTime" :minuteStep="5" placeholder="End time" />
				</div>
			</div>
		</div>

		<!-- Scope -->
		<div class="flex flex-col gap-2">
			<label class="text-[0.8125rem] font-semibold text-color">Scope</label>
			<div class="flex gap-2">
				<button
					type="button"
					class="flex-1 py-2.5 px-3 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color-secondary text-sm font-semibold cursor-pointer transition-all hover:border-primary hover:text-color hover:bg-[var(--egs-content-hover)]"
					:class="{ '!border-primary !text-color !bg-primary/5': scope === 'global' }"
					@click="((scope = 'global'), (showGroupValidation = false))"
				>
					All Groups
				</button>
				<button
					type="button"
					class="flex-1 py-2.5 px-3 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color-secondary text-sm font-semibold cursor-pointer transition-all hover:border-primary hover:text-color hover:bg-[var(--egs-content-hover)]"
					:class="{ '!border-primary !text-color !bg-primary/5': scope === 'group' }"
					@click="scope = 'group'"
				>
					Specific Group
				</button>
			</div>

			<div v-if="scope === 'group'" class="mt-2.5">
				<EGS_Dropdown
					v-model="selectedGroupId"
					:options="userGroups"
					optionLabel="name"
					optionValue="id"
					:disabled="groupsLoading || userGroups.length === 0"
					placeholder="Select a group..."
					class="w-full"
					:class="{ 'dropdown-invalid': showGroupValidation && !selectedGroupId }"
				/>
				<small v-if="groupsLoading" class="block mt-1.5 text-color-secondary text-xs">Loading your groups…</small>
				<small v-else-if="userGroups.length === 0" class="block mt-1.5 text-color-secondary text-xs">No groups found for your account.</small>
				<small v-else-if="showGroupValidation && !selectedGroupId" class="block mt-1.5 text-red-500 text-xs">Please select a group to continue</small>
				<small v-else class="block mt-1.5 text-color-secondary text-xs">Choose a group to apply this entry only within that group.</small>
			</div>
		</div>

		<!-- Recurring -->
		<div class="flex flex-col gap-2">
			<div class="flex items-center justify-between">
				<label class="text-[0.8125rem] font-semibold text-color">Recurring</label>
				<label class="flex items-center gap-2 text-sm text-color cursor-pointer">
					<input type="checkbox" v-model="isRecurring" class="w-4 h-4 accent-primary" />
					<span>Repeat weekly</span>
				</label>
			</div>
			<div v-if="isRecurring" class="mt-2">
				<p class="text-[0.8125rem] text-color-secondary mb-2.5">Select which days of the week this applies to:</p>
				<RecurringPicker v-model="recurringDays" />
			</div>
		</div>

		<!-- Notes -->
		<div class="flex flex-col gap-2">
			<label class="text-[0.8125rem] font-semibold text-color">Notes (optional)</label>
			<textarea
				v-model="notes"
				class="py-2.5 px-3 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color text-sm transition-colors resize-y min-h-[60px] focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
				placeholder="Add a note about this entry..."
				rows="2"
			></textarea>
		</div>

		<!-- Actions -->
		<div class="flex items-center justify-between pt-3 border-t border-surface">
			<button
				v-if="mode === 'edit' && initialData?.id"
				type="button"
				class="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-red-300 bg-[var(--egs-content-background)] px-4 py-2.5 text-sm font-medium text-red-600 transition-all hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/50"
				:disabled="deleting"
				@click="handleDelete"
			>
				<font-awesome-icon :icon="['fas', 'trash']" />
				<span>{{ deleting ? 'Deleting...' : 'Delete' }}</span>
			</button>
			<div class="flex gap-2.5 ml-auto">
				<button
					type="button"
					class="py-2.5 px-4 rounded-lg bg-[var(--egs-content-background)] border border-surface text-color-secondary text-sm font-medium cursor-pointer transition-all hover:bg-[var(--egs-content-hover)]"
					@click="handleCancel"
				>
					Cancel
				</button>
				<button
					type="submit"
					class="py-2.5 px-4 rounded-lg bg-primary border-none text-white text-sm font-medium cursor-pointer transition-all hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
					:disabled="!isValid || saving"
				>
					<span>{{ saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save' }}</span>
				</button>
			</div>
		</div>
	</form>
</template>
